import { Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import {
  MetricsProvider,
  ATTRIBUTE_PERSONA_TYPE_SPEAKER
} from '@studio/metrics';
import {
  getEnvironmentType,
  broadcastTypesMap,
  MEETING_STATUS,
  VENDOR_AGORA
} from '@studio/constants';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import React, { useMemo, useState, useEffect, lazy, Suspense } from 'react';
import _ from 'underscore';
import {
  AppContext,
  getEnvVar,
  MeetingEndedModal,
  AudioInputFailed,
  CreateMeetingError,
  BrandContext,
  useLaunchDarkly,
  UnauthorizedError
} from '@shoflo/polymer';
import { useRequireAuth, useLDPayload } from '@shoflo/studio-redux/hooks';

import { datadogRum } from '@datadog/browser-rum';

import Paths from './AppPaths';
import { LIGHT_THEME } from 'constants/Theme';
import { BROADCAST_TYPE } from 'constants/SearchParams';
import GlobalStyle from './GlobalStyle.styled';
import ThemeContext from 'contexts/ThemeContext';
import { setPresenterId } from 'actions/Broadcast';
import ScreenshareModal from 'containers/modals/Screenshare';
import SettingsModal from 'containers/modals/Settings/Settings';
import GenericSettingsModal from 'containers/modals/Settings/GenericSettings';
import AwaitingKnockApprovalModal from 'containers/modals/AwaitingKnockApproval/AwaitingKnockApproval';
import InvalidJoinRequestModal from 'containers/modals/InvalidJoinRequestModal/InvalidJoinRequestModal';
import { datadogLogs } from '@datadog/browser-logs';
import { getURLPathParams } from '@studio/logger';
import packageJSON from '../package.json';

import { Loading } from '@shoflo/polymer';
import { Redirect } from 'react-router';
import { shouldHaveFeature } from '@studio/feature.flags';
const { version } = packageJSON;

const ReadinessCheckPage = lazy(() =>
  import('containers/pages/ReadinessCheck/ReadinessCheck')
);
const AgoraBroadcast = lazy(() =>
  import('./containers/pages/Broadcast/AgoraBroadcast')
);
const AgoraJoin = lazy(() =>
  import('./containers/pages/JoinV2/Agora/AgoraJoin')
);

const JoinV2 = lazy(() =>
  import('./containers/pages/JoinV2').then(module => ({
    default: module.JoinV2
  }))
);
const IntercomApp = lazy(() =>
  import('@studio/intercom').then(module => ({
    default: module.default
  }))
);
const ChimeBroadcastPage = lazy(() =>
  import('containers/pages/Broadcast/Broadcast')
);

const App = () => {
  const rudderstackDataPlaneUrl = getEnvVar(
    'REACT_APP_RUDDERSTACK_DATA_PLANE_URL'
  );
  const env = getEnvVar('REACT_APP_CVENT_ENV');
  const rudderstackWriteKey = getEnvVar('REACT_APP_RUDDERSTACK_WRITE_KEY');

  const [theme, setTheme] = useState(LIGHT_THEME);
  const user = useSelector(state => state.user, shallowEqual);
  const presenterId = useSelector(state => state.presenterId);
  const themeContext = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  const dispatch = useDispatch();
  const { brandingSettings } = useSelector(
    ({ brandingSettings }) => ({
      brandingSettings
    }),
    shallowEqual
  );

  const broadcastStartOn = useSelector(state => state.broadcast?.startOn);
  const broadcastEndOn = useSelector(state => state.broadcast?.endOn);
  const broadcastTitle = useSelector(state => state.broadcast?.title);
  const externalEventId = useSelector(state => state.event?.externalId);
  const eventTitle = useSelector(state => state.event?.title);
  const eventStartDate = useSelector(state => state.event?.startOn);
  const eventEndDate = useSelector(state => state.event?.endOn);
  const meetingStarted = useSelector(state => state.meeting.started);
  const meetingStatus = useSelector(state => state.meeting.status);
  const accountMappingId = useSelector(state => state.team?.externalId);
  const knock = useSelector(state => state.knock);
  const broadcastExternalId = useSelector(state => state.broadcast?.externalId);

  const requireSpeakerAuth = shouldHaveFeature('studio.requirePresenterAuth');

  // Only enforce authentication check when presenter auth is required
  useRequireAuth(requireSpeakerAuth);

  const meetingType = shouldHaveFeature('studio.meetingVendor');

  let JoinPage = JoinV2;
  let BroadcastPage = ChimeBroadcastPage;

  let isUserInBroadcast = requireSpeakerAuth
    ? meetingStarted
    : meetingStarted && knock;

  if (meetingType === VENDOR_AGORA) {
    isUserInBroadcast = meetingStatus === MEETING_STATUS.STARTED;
    JoinPage = AgoraJoin;
    BroadcastPage = AgoraBroadcast;
  }

  const externalUserId = user?.externalId;
  const userId = user?._id;

  const query = new URLSearchParams(useLocation().search);
  const broadcastType = query.get(BROADCAST_TYPE);
  const appContextValue = {
    isStudioLite: Boolean(broadcastType),
    isPresenterStandalone: !Boolean(broadcastType)
  };

  // remove with studio.requirePresenterAuth
  useEffect(() => {
    FingerprintJS.load().then(fp => {
      fp.get().then(result => {
        const { visitorId } = result;

        dispatch(setPresenterId(visitorId));
      });
    });
  }, [dispatch]);

  useEffect(() => {
    if (externalUserId) {
      datadogRum.setGlobalContextProperty(
        'studio.external.user.id',
        externalUserId
      );
      datadogLogs.setGlobalContextProperty(
        'studio.external.user.id',
        externalUserId
      );
    }
  }, [externalUserId]);

  useEffect(() => {
    if (userId) {
      datadogRum.setGlobalContextProperty('studio.user.id', userId);
      datadogLogs.setGlobalContextProperty('studio.user.id', userId);

      datadogRum.setUser({
        id: userId
      });
    }
  }, [userId]);

  // remove with studio.requirePresenterAuth
  useEffect(() => {
    if (presenterId) {
      datadogRum.setGlobalContextProperty(
        'studio.user.presenterId',
        presenterId
      );
      datadogLogs.setGlobalContextProperty(
        'studio.user.presenterId',
        presenterId
      );
    }
  }, [presenterId]);

  useEffect(() => {
    if (accountMappingId) {
      datadogRum.setGlobalContextProperty(
        'account_mapping.id',
        accountMappingId
      );
      datadogLogs.setGlobalContextProperty(
        'account_mapping.id',
        accountMappingId
      );
    }
  }, [accountMappingId]);

  const pathParams = getURLPathParams();

  const teamId = pathParams?.teamId;
  const eventId = pathParams?.eventId;
  const broadcastId = pathParams?.broadcastId;

  const joinRouteParams = query.size > 0 ? `?${query.toString()}` : '';
  const joinRoute = `/studio-presenter/teams/${teamId}/events/${eventId}/broadcasts/${broadcastId}/join${joinRouteParams}`;

  const speakerUserId = requireSpeakerAuth ? userId : presenterId;

  const metricsProps = useMemo(
    () => ({
      accountMappingId,
      attendeeId: externalUserId,
      studioUserId: speakerUserId,
      persona: ATTRIBUTE_PERSONA_TYPE_SPEAKER,
      rudderstackWriteKey,
      environmentName: getEnvironmentType(env) || 'test',
      rudderstackDataPlaneUrl,
      appVersion: version,
      broadcastId,
      teamId,
      studioEventId: eventId,
      sessionStartDate: new Date(broadcastStartOn),
      sessionEndDate: new Date(broadcastEndOn),
      broadcastType: broadcastTypesMap.get(broadcastType?.toLowerCase()),
      sessionName: broadcastTitle,
      sessionId: broadcastExternalId,
      eventId: externalEventId,
      eventName: eventTitle,
      eventStartDate: new Date(eventStartDate),
      eventEndDate: new Date(eventEndDate),
      meetingType
    }),
    [
      accountMappingId,
      externalUserId,
      speakerUserId,
      rudderstackWriteKey,
      env,
      rudderstackDataPlaneUrl,
      broadcastId,
      teamId,
      eventId,
      broadcastStartOn,
      broadcastEndOn,
      broadcastType,
      broadcastTitle,
      broadcastExternalId,
      externalEventId,
      eventTitle,
      eventStartDate,
      eventEndDate,
      meetingType
    ]
  );

  const intercomProps = {
    appId: getEnvVar('REACT_APP_INTERCOM_APP_ID'),
    intercomIdentityVerificationKey: getEnvVar(
      'REACT_APP_INTERCOM_IDENTITY_VERIFICATION_KEY'
    ),
    user: {
      ...(externalUserId && {
        id: userId,
        firstName: user.firstName,
        lastName: user.lastName,
        emailAddress: user.email,
        persona: ATTRIBUTE_PERSONA_TYPE_SPEAKER
      })
    },
    eventDetails: {
      eventId: pathParams?.eventId
    }
  };

  // remove with studio.requirePresenterAuth
  const defaultUser = {
    _id: 'presenter-anonymous',
    firstName: 'unauthenticated-presenter',
    email: 'presenter-anonymous@shoflo.io',
    createdOn: Date.now()
  };

  const ldKey = getEnvVar('REACT_APP_LAUNCHDARKLY_KEY');
  const ldPayload = useLDPayload({
    requireSpeakerAuth,
    defaultUser
  });

  const isLDInitializedOrTimedOut = useLaunchDarkly(ldPayload, ldKey);

  if (!isLDInitializedOrTimedOut) return <></>;

  return (
    <MetricsProvider {...metricsProps}>
      <AppContext.Provider value={appContextValue}>
        <BrandContext.Provider value={brandingSettings}>
          <ThemeContext.Provider value={themeContext}>
            <ThemeProvider theme={{ id: theme }}>
              {/* ThemeProvider - For styled components - provides theme via props.theme.id */}
              <Suspense fallback={<Loading />}>
                <Route exact path={Paths.BroadcastPage}>
                  {isUserInBroadcast ? (
                    <BroadcastPage />
                  ) : (
                    <Redirect to={joinRoute} />
                  )}
                </Route>
                <Route exact path={Paths.JoinPage} component={JoinPage} />
                <Route
                  exact
                  path={Paths.ReadinessCheckPage}
                  component={ReadinessCheckPage}
                />
              </Suspense>
              {!_.isEmpty(intercomProps.user) && (
                <Suspense fallback={<Loading />}>
                  <IntercomApp
                    appId={intercomProps.appId}
                    intercomIdentityVerificationKey={
                      intercomProps.intercomIdentityVerificationKey
                    }
                    userDetails={intercomProps.user}
                    eventDetails={intercomProps.eventDetails}
                  />
                </Suspense>
              )}
              <GlobalStyle />
              {/* modals */}
              <AwaitingKnockApprovalModal />
              <SettingsModal />
              <GenericSettingsModal />
              <ScreenshareModal />
              <InvalidJoinRequestModal />
              <MeetingEndedModal />
              <CreateMeetingError />
              <UnauthorizedError />
              <AudioInputFailed />
            </ThemeProvider>
          </ThemeContext.Provider>
        </BrandContext.Provider>
      </AppContext.Provider>
    </MetricsProvider>
  );
};

export default App;
